import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, ArrowRight, ArrowLeft, Phone, Building2, Check, ChevronDown, ChevronUp,
  Database, Stethoscope, Activity, Briefcase, Package, LifeBuoy, UtensilsCrossed,
  Coffee, Fan, Handshake, ShieldAlert, BookOpen, GraduationCap, Smile, Pill,
  RefreshCw, Receipt, Star, CalendarCheck, MapPin, Award, ShoppingBag, Home, Eye,
  FileSignature, CreditCard, Dumbbell, User, IdCard, Scissors, BedDouble, Car,
  Compass, PackageCheck, Scale, Clock, Gift, PartyPopper, Tag, Plane, Ticket, Lock,
  type LucideIcon,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useAuth } from '@/hooks/useAuth';
import { businessProfileService, type IndustryValue } from '@/services/business-profile.service';
import { settingsService } from '@/services/settings.service';
import { IndustryPicker } from '@/components/IndustryPicker';
import { industryIcon, industryOption } from '@/lib/industries';
import { whatsappAccountService } from '@/services/whatsapp-account.service';
import { customObjectsService, type TemplateListResponse, type VerticalTemplateSummary } from '@/services/custom-objects.service';
import { ConnectAccountModal } from '@/pages/whatsapp-account/ConnectAccountModal';
import clsx from 'clsx';

type ToneValue = 'formal' | 'friendly' | 'casual';
type Step = 1 | 2 | 3 | 4;

// Same icon set the sidebar uses for custom object types — keep in sync with
// backend templates.config.ts. Unknown names fall back to Database.
const PACK_ICONS: Record<string, LucideIcon> = {
  Database, Stethoscope, Activity, Briefcase, Package, LifeBuoy,
  UtensilsCrossed, Coffee, Fan, Handshake, ShieldAlert, BookOpen, GraduationCap,
  Smile, Pill, RefreshCw, Receipt, Star, CalendarCheck, MapPin,
  Award, ShoppingBag, Home, Eye, FileSignature, CreditCard, Dumbbell,
  User, IdCard, Scissors, BedDouble, Car, Compass, PackageCheck, Scale,
  Clock, Gift, PartyPopper, Tag, Plane, Ticket, Sparkles,
};


// Examples under the business fields follow the industry — a fitness studio used to see
// "We sell custom-built mobile apps…".
const EXAMPLES: Record<string, { oneLiner: string; products: string }> = {
  'Food & Hospitality': { oneLiner: 'A family restaurant in Dubai Marina serving Levantine grills.', products: 'Mixed grill platters, mezze, family meals, catering, delivery…' },
  Technology: { oneLiner: 'We build custom mobile apps for SMBs in the GCC.', products: 'Mobile app development, web development, AI agents, consultations…' },
  'Retail & Electronics': { oneLiner: 'Phones, accessories and repairs in Deira City Centre.', products: 'iPhone and Samsung phones, cases, chargers, screen repair, trade-ins…' },
  'Brands & Manufacturing': { oneLiner: 'An abaya brand selling online across the GCC.', products: 'Everyday abayas, occasion wear, custom tailoring, gift boxes…' },
  'Beauty & Wellness': { oneLiner: 'A ladies salon in Al Barsha.', products: 'Hair colour, keratin, nails, facials, bridal packages…' },
  'Health & Medical': { oneLiner: 'A dental clinic in Jumeirah.', products: 'Check-ups, cleaning, whitening, braces, implants…' },
  'Real Estate': { oneLiner: 'We rent and sell apartments and villas in Dubai.', products: 'Studio to 3-bed rentals, off-plan units, villas, property management…' },
  Education: { oneLiner: 'An after-school tutoring centre in Sharjah.', products: 'Maths and science tutoring, IELTS prep, summer courses…' },
  'Fitness & Sport': { oneLiner: 'A boutique gym in Business Bay.', products: 'Monthly memberships, personal training, group classes, day passes…' },
  'Travel & Hospitality': { oneLiner: 'A travel agency for holidays and Umrah packages.', products: 'Holiday packages, flights, hotels, visas, Umrah trips…' },
  Commerce: { oneLiner: 'An online store delivering home décor across the UAE.', products: 'Cushions, rugs, lamps, wall art, same-day delivery…' },
  Professional: { oneLiner: 'An accounting firm for UAE SMEs.', products: 'Bookkeeping, VAT returns, corporate tax, audit support…' },
  Automotive: { oneLiner: 'A car rental company in Dubai.', products: 'Daily and monthly rentals, luxury cars, chauffeur service…' },
  Events: { oneLiner: 'We plan weddings and corporate events in the UAE.', products: 'Wedding planning, venues, décor, catering, photography…' },
  'Logistics & Delivery': { oneLiner: 'Same-day courier service across Dubai.', products: 'Same-day delivery, cash on delivery, warehousing…' },
  Communications: { oneLiner: 'A telecom reseller for businesses.', products: 'Business lines, SIM plans, call centre setup…' },
};
const DEFAULT_EXAMPLE = { oneLiner: 'What your business does, in one sentence.', products: 'The products and services customers ask you about…' };

export default function Onboarding() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === 'company_admin';

  const [step, setStep] = useState<Step>(1);
  const [busy, setBusy] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [hasWaAccount, setHasWaAccount] = useState(false);

  // Step 1 — business profile. No default industry: a pre-selected one got
  // saved by everyone who just pressed Continue.
  const [industry, setIndustry] = useState<IndustryValue | ''>('');
  const example = EXAMPLES[industryOption(industry)?.group ?? ''] ?? DEFAULT_EXAMPLE;
  const [industryOther, setIndustryOther] = useState('');
  // Set when the platform already assigned this company's industry.
  const [industryLocked, setIndustryLocked] = useState(false);
  const [workspaceLoaded, setWorkspaceLoaded] = useState(false);
  const [triedContinue, setTriedContinue] = useState(false);
  const [oneLiner, setOneLiner] = useState('');
  const [productsServices, setProductsServices] = useState('');
  const [tone, setTone] = useState<ToneValue>('friendly');

  // Step 2 — industry pack
  const [templates, setTemplates] = useState<TemplateListResponse | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [installing, setInstalling] = useState<string | null>(null);
  const [installedIds, setInstalledIds] = useState<string[]>([]);
  const [showOtherPacks, setShowOtherPacks] = useState(false);

  // On mount: skip onboarding if profile already exists; pre-check WA accounts;
  // pick up the industry the platform assigned, if any.
  useEffect(() => {
    (async () => {
      const [profile, accounts, workspace] = await Promise.all([
        businessProfileService.getByCompany(),
        whatsappAccountService.list().catch(() => []),
        settingsService.workspace().catch(() => null),
      ]);
      if (profile) {
        nav('/dashboard', { replace: true });
        return;
      }
      setHasWaAccount(accounts.length > 0);
      if (workspace?.industryLocked && workspace.industry) {
        setIndustry(workspace.industry);
        setIndustryLocked(true);
      }
      setWorkspaceLoaded(true);
    })();
  }, [nav]);

  if (!isAdmin) {
    // Non-admin landed on onboarding — bounce them home.
    return null;
  }

  const saveProfileAndAdvance = async () => {
    setTriedContinue(true);
    if (!industry) return;
    setBusy(true);
    try {
      await businessProfileService.upsert({
        // The server owns an assigned industry and ignores this; send it only for a first pick.
        ...(industryLocked ? {} : { industry }),
        ...(industry === 'other' && industryOther.trim() ? { industryOther: industryOther.trim() } : {}),
        oneLiner: oneLiner.trim() || undefined,
        productsServices: productsServices.trim() || undefined,
        tone,
        // Sensible defaults — user can tweak later in Settings → Business Profile.
        primaryLanguage: 'en',
        responseSlaMinutes: 60,
      });
      toast.success('Business profile saved');
      setStep(2);
      // Pass the freshly-saved industry explicitly — avoids racing the
      // profile write on the backend's auto-detection.
      setLoadingTemplates(true);
      customObjectsService
        .listTemplates(industry)
        .then(setTemplates)
        .catch(() => undefined)
        .finally(() => setLoadingTemplates(false));
    } catch (e: any) {
      // axios interceptor already toasted backend message
    } finally { setBusy(false); }
  };

  const installPack = async (templateId: string) => {
    setInstalling(templateId);
    try {
      const r = await customObjectsService.installTemplate(templateId);
      setInstalledIds((prev) => [...prev, templateId]);
      if (r.installed.length === 0 && r.skipped.length > 0) {
        toast(`Already set up: ${r.skipped.join(', ')}`);
      } else {
        toast.success(`Added ${r.installed.length} data type${r.installed.length === 1 ? '' : 's'} for your business`);
      }
    } catch (e: any) {
      if (!e?.response) toast.error('Install failed');
    } finally { setInstalling(null); }
  };

  const onConnected = async () => {
    setConnectOpen(false);
    setHasWaAccount(true);
    toast.success('WhatsApp account connected');
  };

  const finish = () => {
    nav('/dashboard', { replace: true });
  };

  const recommendedPacks = templates?.recommended ?? [];
  const otherPacks = templates?.other ?? [];

  const renderPackCard = (tpl: VerticalTemplateSummary) => {
    const installed = installedIds.includes(tpl.id);
    const Icon = PACK_ICONS[tpl.types[0]?.icon ?? 'Database'] ?? Database;
    return (
      <button
        key={tpl.id}
        type="button"
        disabled={installed || installing !== null}
        onClick={() => installPack(tpl.id)}
        className={clsx(
          'rounded-xl border bg-surface px-4 py-3 text-left transition-colors',
          installed
            ? 'border-success/40 bg-success-soft/30'
            : 'border-border hover:border-primary/50 hover:bg-surface-alt',
          installing !== null && !installed && 'opacity-60',
        )}
      >
        <div className="flex items-start gap-3">
          <div className={clsx(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
            installed ? 'bg-success-soft text-success' : 'bg-primary-soft text-primary',
          )}>
            {installed ? <Check size={16} /> : <Icon size={16} />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
              <span className="truncate">{tpl.label}</span>
              {installing === tpl.id && <span className="text-2xs font-normal text-text-muted">installing…</span>}
              {installed && <span className="rounded-full bg-success-soft px-1.5 py-0.5 text-2xs font-medium text-success">installed</span>}
            </div>
            {/* "What you get" — the object types this pack creates */}
            <div className="mt-0.5 text-xs text-text-muted">
              {tpl.types.map((t) => t.pluralLabel).join(' + ')}
            </div>
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-bg bg-paper-noise">
      <div className="pointer-events-none absolute -right-32 -top-40 h-[28rem] w-[28rem] rounded-full bg-gold-gradient-soft blur-3xl" />
      <div className="pointer-events-none absolute -left-24 bottom-[-10rem] h-[24rem] w-[24rem] rounded-full bg-gold-gradient-soft blur-3xl" />

      <div className="absolute right-5 top-5 z-20"><ThemeToggle /></div>
      <div className="absolute left-7 top-7 z-20 flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gold-gradient text-on-primary shadow-glow">
          <Sparkles size={16} strokeWidth={2.5} />
        </div>
        <div className="font-display text-[15px] font-bold tracking-tight text-text-primary">SlightorCRM</div>
      </div>

      <div className="relative flex min-h-screen items-center justify-center px-6 py-16">
        <div className="w-full max-w-2xl rounded-3xl border border-border bg-surface/85 p-8 shadow-elevated backdrop-blur-xl sm:p-10">
          <Stepper step={step} />

          {step === 1 && (
            <div className="mt-6 space-y-1">
              <div className="flex items-center gap-2 mb-2">
                <Building2 size={18} className="text-primary" />
                <h1 className="font-display text-[22px] font-bold tracking-tight text-text-primary">Tell us about your business</h1>
              </div>
              <p className="text-sm text-text-muted mb-5">
                We'll use this to power your AI chatbot — so it answers questions in your industry's voice.
              </p>

              <FormField
                label="Industry"
                required
                error={triedContinue && !industry ? 'Choose the industry closest to your business' : undefined}
              >
                {!workspaceLoaded ? (
                  <div className="skeleton h-[58px] rounded-xl" />
                ) : industryLocked && industry ? (
                  <LockedIndustry industry={industry} />
                ) : (
                  <IndustryPicker
                    value={industry}
                    onChange={(v) => setIndustry(v as IndustryValue)}
                    invalid={triedContinue && !industry}
                  />
                )}
              </FormField>

              {industry === 'other' && !industryLocked && (
                <FormField label="Describe your industry" hint="A short phrase, e.g. “Luxury car detailing”">
                  <Input
                    placeholder="What kind of business is this?"
                    value={industryOther}
                    onChange={(e) => setIndustryOther(e.target.value)}
                  />
                </FormField>
              )}

              <FormField label="One-liner" hint="What does your business do, in one sentence?">
                <Input
                  placeholder={example.oneLiner}
                  value={oneLiner}
                  onChange={(e) => setOneLiner(e.target.value)}
                />
              </FormField>

              <FormField label="Products & services" hint="List the things customers can ask about — bot uses this to disambiguate.">
                <Textarea
                  rows={3}
                  placeholder={example.products}
                  value={productsServices}
                  onChange={(e) => setProductsServices(e.target.value)}
                />
              </FormField>

              <FormField label="Conversation tone">
                <Select value={tone} onChange={(e) => setTone(e.target.value as ToneValue)}>
                  <option value="formal">Formal</option>
                  <option value="friendly">Friendly</option>
                  <option value="casual">Casual</option>
                </Select>
              </FormField>

              <div className="flex justify-end mt-6">
                <Button
                  onClick={saveProfileAndAdvance}
                  loading={busy}
                  disabled={!workspaceLoaded}
                  iconRight={<ArrowRight size={15} />}
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="mt-6 space-y-1">
              <div className="flex items-center gap-2 mb-2">
                <Database size={18} className="text-primary" />
                <h1 className="font-display text-[22px] font-bold tracking-tight text-text-primary">Set up my industry</h1>
              </div>
              <p className="text-sm text-text-muted mb-5">
                One click installs ready-made data types for your business — like Menu items for a restaurant
                or Doctors for a clinic. Your WhatsApp AI agent will adapt to them automatically.
                You can edit everything later under Custom Objects.
              </p>

              {loadingTemplates && (
                <div className="grid gap-3 sm:grid-cols-2">
                  {[0, 1].map((i) => <div key={i} className="skeleton h-20 rounded-xl" />)}
                </div>
              )}

              {!loadingTemplates && recommendedPacks.length > 0 && (
                <div className="grid gap-3 sm:grid-cols-2">
                  {recommendedPacks.map(renderPackCard)}
                </div>
              )}

              {!loadingTemplates && recommendedPacks.length === 0 && (
                <div className="rounded-xl border border-dashed border-border bg-surface-alt/40 px-4 py-5 text-sm text-text-muted">
                  We don't have a ready-made pack for your industry yet — pick one from another
                  industry below if it fits, or build your own under Custom Objects later.
                </div>
              )}

              {!loadingTemplates && otherPacks.length > 0 && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setShowOtherPacks((v) => !v)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-text-secondary hover:text-primary"
                  >
                    {showOtherPacks ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    {showOtherPacks ? 'Hide other industries' : `Browse ${otherPacks.length} other industries`}
                  </button>
                  {showOtherPacks && (
                    <div className="mt-3 grid max-h-72 gap-3 overflow-y-auto sm:grid-cols-2">
                      {otherPacks.map(renderPackCard)}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-between mt-6">
                <Button variant="ghost" onClick={() => setStep(1)} iconLeft={<ArrowLeft size={15} />}>Back</Button>
                <Button onClick={() => setStep(3)} iconRight={<ArrowRight size={15} />}>
                  {installedIds.length > 0 ? 'Continue' : 'Skip for now'}
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="mt-6 space-y-1">
              <div className="flex items-center gap-2 mb-2">
                <Phone size={18} className="text-primary" />
                <h1 className="font-display text-[22px] font-bold tracking-tight text-text-primary">Connect WhatsApp Business</h1>
              </div>
              <p className="text-sm text-text-muted mb-5">
                Link your WhatsApp Business Account so SlightorCRM can send templates and receive customer messages on your behalf. You can connect more accounts later.
              </p>

              <div className="rounded-xl border border-border bg-surface-alt/40 p-5">
                {hasWaAccount ? (
                  <div className="flex items-center gap-2 text-sm text-success">
                    <Check size={16} /> You already have a WhatsApp account connected.
                  </div>
                ) : (
                  <p className="text-sm text-text-secondary">
                    No WhatsApp accounts yet. Connect one now (recommended), or skip and do it later from Settings → WhatsApp → Accounts.
                  </p>
                )}
                <div className="mt-4 flex gap-2">
                  <Button variant="secondary" onClick={() => setConnectOpen(true)}>
                    {hasWaAccount ? 'Connect another' : 'Connect WhatsApp'}
                  </Button>
                </div>
              </div>

              <div className="flex justify-between mt-6">
                <Button variant="ghost" onClick={() => setStep(2)} iconLeft={<ArrowLeft size={15} />}>Back</Button>
                <Button onClick={() => setStep(4)} iconRight={<ArrowRight size={15} />}>
                  {hasWaAccount ? 'Continue' : 'Skip for now'}
                </Button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="mt-6 space-y-1">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={18} className="text-primary" />
                <h1 className="font-display text-[22px] font-bold tracking-tight text-text-primary">You're all set</h1>
              </div>
              <p className="text-sm text-text-muted mb-5">
                We've saved your business profile. Your bot flows, templates, and AI features will use it to stay on-brand. You can refine details anytime in Settings.
              </p>

              <div className="rounded-xl border border-border bg-surface-alt/40 p-5 space-y-2 text-sm">
                <div className="flex items-center gap-2"><Check size={14} className="text-success" /> Business profile created</div>
                <div className="flex items-center gap-2">
                  {installedIds.length > 0
                    ? <><Check size={14} className="text-success" /> Industry pack installed — your AI agent adapts automatically</>
                    : <><span className="text-text-muted">○</span><span className="text-text-muted">Industry pack — skipped (install anytime from Custom Objects)</span></>}
                </div>
                <div className="flex items-center gap-2">
                  {hasWaAccount
                    ? <><Check size={14} className="text-success" /> WhatsApp connected</>
                    : <><span className="text-text-muted">○</span><span className="text-text-muted">WhatsApp connection — skipped</span></>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-text-muted">○</span><span className="text-text-muted">AI provider — configure in Settings → AI providers when ready</span>
                </div>
              </div>

              <div className="flex justify-between mt-6">
                <Button variant="ghost" onClick={() => setStep(3)} iconLeft={<ArrowLeft size={15} />}>Back</Button>
                <Button onClick={finish} iconRight={<ArrowRight size={15} />}>Go to dashboard</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConnectAccountModal open={connectOpen} onClose={() => setConnectOpen(false)} onConnected={onConnected} />
    </div>
  );
}

/** The industry the platform assigned — shown, not editable. */
function LockedIndustry({ industry }: { industry: IndustryValue }) {
  const opt = industryOption(industry);
  const Icon = industryIcon(industry);
  return (
    <div>
      <div className="flex items-center gap-3.5 rounded-xl border border-primary/30 bg-primary-soft px-3.5 py-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gold-gradient text-on-primary shadow-glow">
          <Icon size={19} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-display text-[16px] font-semibold text-text-primary">{opt?.label ?? industry}</div>
          <div className="truncate text-xs text-text-muted">{opt?.group}</div>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border bg-surface px-2.5 py-1 text-2xs font-semibold text-text-secondary">
          <Lock size={11} /> Set for you
        </span>
      </div>
      <p className="mt-1.5 text-2xs text-text-muted">
        Your account manager set this up. Your AI assistant, departments and lead fields are tailored to it — contact support to change it.
      </p>
    </div>
  );
}

function Stepper({ step }: { step: Step }) {
  const dots = [
    { n: 1, label: 'Business' },
    { n: 2, label: 'Industry' },
    { n: 3, label: 'WhatsApp' },
    { n: 4, label: 'Done' },
  ];
  return (
    <div className="flex items-center gap-3">
      {dots.map((d, i) => (
        <div key={d.n} className="flex items-center gap-3">
          <div className="flex flex-col items-center gap-1">
            <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
              step === d.n ? 'bg-gold-gradient text-on-primary shadow-glow' :
              step > d.n ? 'bg-success-soft text-success' :
              'bg-surface-alt text-text-muted'
            }`}>
              {step > d.n ? <Check size={13} /> : d.n}
            </div>
            <span className={`text-2xs font-medium ${step >= d.n ? 'text-text-primary' : 'text-text-muted'}`}>{d.label}</span>
          </div>
          {i < dots.length - 1 && <div className={`h-px w-8 ${step > d.n ? 'bg-success' : 'bg-border'}`} />}
        </div>
      ))}
    </div>
  );
}
